/** Default when company timezone is not loaded yet. */
export const DEFAULT_ACTIVITY_TIMEZONE = 'Europe/Berlin';

export function wallClockInTimeZone(
  instant: Date,
  timeZone: string,
): { y: number; mo: number; d: number; hh: number; mi: number; se: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  }).formatToParts(instant);
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  let hh = n('hour');
  if (hh === 24) hh = 0;
  return { y: n('year'), mo: n('month'), d: n('day'), hh, mi: n('minute'), se: n('second') };
}

const pad2 = (n: number) => String(n).padStart(2, '0');

export function formatWallClockYmd(
  instant: Date,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): string {
  const w = wallClockInTimeZone(instant, timeZone);
  return `${w.y}-${pad2(w.mo)}-${pad2(w.d)}`;
}

export function formatWallClockHm(
  instant: Date,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): string {
  const w = wallClockInTimeZone(instant, timeZone);
  return `${pad2(w.hh)}:${pad2(w.mi)}`;
}

export function wallClockToUtcMs(
  y: number,
  mo: number,
  d: number,
  hh: number,
  mi: number,
  se: number,
  timeZone: string,
): number {
  let utc = Date.UTC(y, mo - 1, d, hh, mi, se);
  for (let i = 0; i < 4; i++) {
    const got = wallClockInTimeZone(new Date(utc), timeZone);
    const want = Date.UTC(y, mo - 1, d, hh, mi, se);
    const gotMs = Date.UTC(got.y, got.mo - 1, got.d, got.hh, got.mi, got.se);
    utc += want - gotMs;
  }
  return utc;
}

function offsetMinutesAt(instant: Date, timeZone: string): number {
  const utc = new Date(instant.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tz = new Date(instant.toLocaleString('en-US', { timeZone }));
  return Math.round((tz.getTime() - utc.getTime()) / 60000);
}

/** Instant → ISO with offset in the given IANA zone (not browser local). */
export function formatIsoWithTimeZoneOffset(
  d: Date,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): string {
  const w = wallClockInTimeZone(d, timeZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  const offMin = offsetMinutesAt(d, timeZone);
  const sign = offMin >= 0 ? '+' : '-';
  const abs = Math.abs(offMin);
  const oh = pad(Math.floor(abs / 60));
  const om = pad(abs % 60);
  return `${w.y}-${pad(w.mo)}-${pad(w.d)}T${pad(w.hh)}:${pad(w.mi)}:${pad(w.se)}${sign}${oh}:${om}`;
}

/** @deprecated Prefer formatIsoWithTimeZoneOffset — kept for call-site renames. */
export function formatIsoWithLocalOffset(
  d: Date,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): string {
  return formatIsoWithTimeZoneOffset(d, timeZone);
}

/** Company wall clock → ISO-String with zone offset (z. B. 2026-05-21T07:00:00+02:00). */
export function localWallTimeToIso(
  dateKey: string,
  time: string,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): string {
  const [y, mo, d] = dateKey.slice(0, 10).split('-').map((x) => parseInt(x, 10));
  const [hh, mm] = (time || '00:00').split(':').map((x) => parseInt(x, 10));
  const utcMs = wallClockToUtcMs(y || 1970, mo || 1, d || 1, hh || 0, mm || 0, 0, timeZone);
  return formatIsoWithTimeZoneOffset(new Date(utcMs), timeZone);
}

/** ISO / DB-Wert → Datum (YYYY-MM-DD) und HH:mm in der Firmenzeitzone. */
export function isoToLocalDateAndTime(
  iso: string | null | undefined,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): { dateKey: string; time: string } {
  if (!iso) {
    const now = new Date();
    return { dateKey: formatWallClockYmd(now, timeZone), time: '09:00' };
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    const now = new Date();
    return { dateKey: formatWallClockYmd(now, timeZone), time: '09:00' };
  }
  return {
    dateKey: formatWallClockYmd(d, timeZone),
    time: formatWallClockHm(d, timeZone),
  };
}

export function isoToLocalHourFraction(
  iso: string,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): number {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  const w = wallClockInTimeZone(d, timeZone);
  return w.hh + w.mi / 60;
}

export function localDateKeyFromDate(
  date: Date,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): string {
  return formatWallClockYmd(date, timeZone);
}

/** YYYY-MM-DD from the Date's local calendar fields (Plantafel column / focus day). */
export function browserLocalYmd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Tagesanfang in Firmenzeitzone für einen Kalenderschlüssel `YYYY-MM-DD`. */
export function ymdDayStartIso(
  ymd: string,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd).trim().slice(0, 10));
  const y = m ? Number(m[1]) : 1970;
  const mo = m ? Number(m[2]) : 1;
  const day = m ? Number(m[3]) : 1;
  const utcMs = wallClockToUtcMs(y, mo, day, 0, 0, 0, timeZone);
  return formatIsoWithTimeZoneOffset(new Date(utcMs), timeZone);
}

/** Tagesende in Firmenzeitzone für einen Kalenderschlüssel `YYYY-MM-DD`. */
export function ymdDayEndIso(
  ymd: string,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd).trim().slice(0, 10));
  const y = m ? Number(m[1]) : 1970;
  const mo = m ? Number(m[2]) : 1;
  const day = m ? Number(m[3]) : 1;
  const utcMs = wallClockToUtcMs(y, mo, day, 23, 59, 59, timeZone) + 999;
  return formatIsoWithTimeZoneOffset(new Date(utcMs), timeZone);
}

/** Tagesanfang in Firmenzeitzone als ISO (Wanduhr-Tag des Instants). */
export function localDayStartIso(
  d: Date,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): string {
  return ymdDayStartIso(formatWallClockYmd(d, timeZone), timeZone);
}

export function localDayEndIso(
  d: Date,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): string {
  return ymdDayEndIso(formatWallClockYmd(d, timeZone), timeZone);
}

/** Date-ähnliches Instant aus Wanduhrteilen in der Firmenzeitzone. */
export function localDateTimeFromParts(
  dateKey: string,
  hourFraction: number,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): Date {
  const [y, mo, d] = dateKey.slice(0, 10).split('-').map((x) => parseInt(x, 10));
  const totalMins = Math.round(hourFraction * 60);
  const hh = Math.floor(totalMins / 60);
  const mm = totalMins % 60;
  return new Date(wallClockToUtcMs(y || 1970, mo || 1, d || 1, hh, mm, 0, timeZone));
}

/** Lokales Datum + HH:mm → Instant in Firmenzeitzone. */
export function localDateAndTimeToDate(
  dateKey: string,
  time: string,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): Date {
  const [y, mo, d] = dateKey.slice(0, 10).split('-').map((x) => parseInt(x, 10));
  const [hh, mm] = (time || '00:00').split(':').map((x) => parseInt(x, 10));
  return new Date(
    wallClockToUtcMs(y || 1970, (mo || 1), d || 1, hh || 0, mm || 0, 0, timeZone),
  );
}

/** True nur bei vollständiger Wanduhrzeit `HH:mm` (Stunden 0–23, Minuten 0–59). */
export function isCompleteWallTime(time: string): boolean {
  if (!/^\d{2}:\d{2}$/.test(time)) return false;
  const [hh, mm] = time.split(':').map((x) => parseInt(x, 10));
  return (
    Number.isFinite(hh) &&
    Number.isFinite(mm) &&
    hh >= 0 &&
    hh <= 23 &&
    mm >= 0 &&
    mm <= 59
  );
}

/**
 * Verschiebt die Endzeit um dieselbe Dauer, wenn die Startzeit geändert wird.
 * Unvollständige Zeiten lassen das Ende unverändert.
 */
export function shiftEndPreservingDuration(params: {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  nextStartTime: string;
  fallbackDurationMs?: number;
  timeZone?: string;
}): { startTime: string; endDate: string; endTime: string } {
  const timeZone = params.timeZone ?? DEFAULT_ACTIVITY_TIMEZONE;
  if (
    !isCompleteWallTime(params.nextStartTime) ||
    !isCompleteWallTime(params.startTime) ||
    !isCompleteWallTime(params.endTime)
  ) {
    return {
      startTime: params.nextStartTime,
      endDate: params.endDate,
      endTime: params.endTime,
    };
  }

  const fallback = params.fallbackDurationMs ?? 60 * 60 * 1000;
  const oldStart = localDateAndTimeToDate(params.startDate, params.startTime, timeZone);
  const oldEnd = localDateAndTimeToDate(params.endDate, params.endTime, timeZone);
  let durationMs = oldEnd.getTime() - oldStart.getTime();
  if (!Number.isFinite(durationMs) || durationMs <= 0) durationMs = fallback;

  const newStart = localDateAndTimeToDate(params.startDate, params.nextStartTime, timeZone);
  if (Number.isNaN(newStart.getTime())) {
    return {
      startTime: params.nextStartTime,
      endDate: params.endDate,
      endTime: params.endTime,
    };
  }

  const newEnd = new Date(newStart.getTime() + durationMs);
  return {
    startTime: params.nextStartTime,
    endDate: formatWallClockYmd(newEnd, timeZone),
    endTime: formatWallClockHm(newEnd, timeZone),
  };
}

/** Hour fraction (0–24) of an instant in the company timezone. */
export function instantToHourFraction(
  instant: Date,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): number {
  const w = wallClockInTimeZone(instant, timeZone);
  return w.hh + w.mi / 60 + w.se / 3600;
}

/** Add calendar days to a `YYYY-MM-DD` key without browser-timezone parsing. */
export function addCalendarDaysYmd(ymd: string, delta: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd).trim().slice(0, 10));
  if (!m) return ymd;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + delta));
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/**
 * Whole-day index of `instant` (company calendar) relative to Plantafel columns.
 * Column 0 is the local calendar day of `viewportStart` (browser fields), not the
 * company YMD of that Instant — so Tag/Woche labels stay aligned with blocks.
 */
export function dayIndexInWallClockViewport(
  viewportStart: Date,
  instant: Date,
  viewMode: 'tag' | 'woche' | 'monat',
  timeZone: string,
  monthDayCount?: number,
): number {
  const vsYmd = browserLocalYmd(viewportStart);
  const tYmd = formatWallClockYmd(instant, timeZone);
  const vsParts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(vsYmd);
  const tParts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(tYmd);
  if (!vsParts || !tParts) return -1;
  const vsMs = Date.UTC(Number(vsParts[1]), Number(vsParts[2]) - 1, Number(vsParts[3]));
  const tMs = Date.UTC(Number(tParts[1]), Number(tParts[2]) - 1, Number(tParts[3]));
  const diff = Math.round((tMs - vsMs) / 86400000);
  if (viewMode === 'tag') return diff === 0 ? 0 : -1;
  if (viewMode === 'monat') {
    const limit = monthDayCount ?? 35;
    return diff < 0 || diff >= limit ? -1 : diff;
  }
  return diff < 0 || diff > 6 ? -1 : diff;
}
